/**
 * TabHelp — small `?` icon next to a tab/section title. Clicking it pops
 * a panel with 2–4 contextual tips/FAQs tailored to that tab. Keeps the
 * help close to where the user actually needs it without leaving the page.
 *
 * Usage:
 *   <TabHelp
 *     title="Vehicle Fleet"
 *     items={[
 *       { q: 'Who gets a company vehicle?', a: 'Anyone with ...' },
 *       ...
 *     ]}
 *   />
 */
import { useEffect, useRef, useState } from 'react';
import { HelpCircle, X } from 'lucide-react';

export default function TabHelp({ title, items = [], testid = 'tab-help' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!items.length) return null;

  return (
    <div className="relative inline-block" ref={ref} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full hover:bg-zinc-100 transition"
        aria-label="Help & FAQs"
        title="Help & FAQs"
        data-testid={`${testid}-button`}
        style={{ color: '#C9A962' }}
      >
        <HelpCircle className="h-5 w-5" />
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-80 sm:w-96 rounded-lg border bg-white p-4 shadow-xl"
          style={{ borderColor: '#C9A962' }}
          data-testid={`${testid}-panel`}
        >
          <div className="flex items-start justify-between mb-2 pb-2 border-b" style={{ borderColor: '#e8e2cf' }}>
            <p
              className="text-xs uppercase tracking-wider font-bold"
              style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {title} · Help
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-zinc-700"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-3">
            {items.map((it, i) => (
              <details key={i} className="group">
                <summary
                  className="text-sm font-bold cursor-pointer list-none flex items-start gap-2"
                  style={{ color: '#0a0a0a' }}
                >
                  <span className="text-[#C9A962] font-black">·</span> {it.q}
                </summary>
                <p className="mt-1 ml-3 text-[13px] leading-snug text-zinc-600">{it.a}</p>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
